import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideEnvironmentInitializer,
  Provider,
  ProviderToken,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { SERVICEMESH_MOCK_API_DEFAULT_DELAY, mockApiInterceptor } from '@helpers/lib/mock-api';
import { ServicemeshConfig } from '@helpers/services/config';
import { SERVICEMESH_CONFIG } from '@helpers/services/config/config.constants';
import { ServicemeshConfirmationService } from '@helpers/services/confirmation';
import {
  servicemeshLoadingInterceptor,
  ServicemeshLoadingService,
} from '@helpers/services/loading';
import { ServicemeshMediaWatcherService } from '@helpers/services/media-watcher';
import { ServicemeshPlatformService } from '@helpers/services/platform';
import { ServicemeshSplashScreenService } from '@helpers/services/splash-screen';
import { GraphqlHostService, ServicemeshUtilsService } from '@helpers/services/utils';

export type ServicemeshProviderConfig = {
  mockApi?: {
    delay?: number;
    services?: unknown[];
  };
  servicemesh?: ServicemeshConfig;
};

/**
 * Servicemesh provider
 */
export const provideServicemesh = (
  config: ServicemeshProviderConfig
): Array<Provider | EnvironmentProviders> => {
  // Base providers
  const providers: Array<Provider | EnvironmentProviders> = [
    // NOTE: Angular Material v21 removed MATERIAL_SANITY_CHECKS and the sanity-check
    // system entirely, so the previous "disable theme sanity check" provider is gone.
    {
      // Use the 'fill' appearance on Angular Material form fields by default
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
      },
    },
    {
      provide: SERVICEMESH_MOCK_API_DEFAULT_DELAY,
      useValue: config?.mockApi?.delay ?? 0,
    },
    {
      provide: SERVICEMESH_CONFIG,
      useValue: config?.servicemesh ?? {},
    },

    importProvidersFrom(MatDialogModule),
    provideEnvironmentInitializer(() => inject(ServicemeshConfirmationService)),

    provideHttpClient(withInterceptors([servicemeshLoadingInterceptor])),
    provideEnvironmentInitializer(() => inject(ServicemeshLoadingService)),

    provideEnvironmentInitializer(() => inject(ServicemeshMediaWatcherService)),
    provideEnvironmentInitializer(() => inject(ServicemeshPlatformService)),
    provideEnvironmentInitializer(() => inject(ServicemeshSplashScreenService)),
    provideEnvironmentInitializer(() => inject(ServicemeshUtilsService)),
    provideEnvironmentInitializer(() => inject(GraphqlHostService)),
  ];

  // Mock Api services
  if (config?.mockApi?.services) {
    providers.push(
      provideHttpClient(withInterceptors([mockApiInterceptor])),
      // Eagerly instantiate the registered mock-api services so they self-register
      // before the app starts (replaces the old APP_INITIALIZER `deps` instantiation).
      provideAppInitializer(() => {
        config.mockApi?.services?.forEach((service) =>
          inject(service as ProviderToken<unknown>)
        );
      })
    );
  }

  // Return the providers
  return providers;
};
